import {
  Attachment,
  BotMessage,
  Chat,
  CustomerMessage,
  CustomerMessageSubtype,
} from "@/types/chat"
import {
  ClientException,
  ClientRateLimitException,
} from "@/types/client-exceptions"
import { InspirationRef, PlaceRef } from "@/types/entity"
import { SimpleHttpError } from "@/types/http"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { buildClientAxios } from "@/lib/client-axios"
import { chatsApi } from "@/lib/storage/api/chats-api"
import {
  incrementSessionChatMessageCount,
  selectEmbeddedGeoHint,
} from "@/lib/storage/app-slice"
import { toastManager } from "@/lib/toast"
import { assert } from "@/lib/utils/assertions"
import {
  sendAnalyticsEvent,
  setAnalyticsUserProperties,
} from "../analytics/amplitude-utils"
import { ChatApi } from "../chat-api"
import { wait } from "../promise-utils"
import { StreamedMessageListener } from "../streamed-message-listener"
import { showRegisterGateDialog } from "./auth-thunks"
import {
  selectChatInputHasValue,
  setFilteredPlaceType,
} from "./chat-mode-slice"
import {
  markChatTabEngaged,
  markChatTabEngagedFromChat,
} from "./chat-tab-outside-nav"
import {
  removePendingBotResponse,
  selectStreamIdsBotIsRespondingToMe,
} from "./presence-slice"
import { AppDispatch, RootState } from "./redux-store"
import { selectChatBotVersion } from "./ui-settings-slice"
import userSlice, {
  isListeningToStreams,
  selectChatId,
  selectHasInProgressMessage,
  selectIsCustomerMessagePending,
  selectIsGroupChat,
  selectIsMindtripUser,
  selectLastCustomerMessage,
  selectLastPersistentBotMessage,
  selectLocationOfInterest,
  selectUserIsAnonymous,
} from "./user-slice"
import { selectVoiceModeOpen, voiceChatActions } from "./voice-chat-slice"

interface SendMessageParams {
  messageString: string
  hint?: string
  attachments?: Attachment[]
  fromInspiration?: InspirationRef
  subType?: CustomerMessageSubtype
  entityRef?: PlaceRef | null
  entityRefs?: string[]
}
interface SendOrEditMessageParams extends SendMessageParams {
  messageId?: number
}

interface FollowUpContext {
  chatId: number
  lastBotMessage: BotMessage
  lastCustomerMessage?: CustomerMessage
}

export const sendMessage = createAsyncThunk<
  CustomerMessage | void,
  SendMessageParams,
  {
    dispatch: AppDispatch
    state: RootState
  }
>("messageThunks/sendOrEditMessage", async (params, { dispatch }) => {
  await dispatch(sendOrEditMessage(params))
})

// Sends a customer message and begins streaming the bot's
// response. Returns the new customer message if one could
// be created. Throws if not, or returns void if the message
// can't be created but the problem has been handled (e.g.
// it's an anon user who has hit the free limit and
//
export const sendOrEditMessage = createAsyncThunk<
  CustomerMessage | void,
  SendOrEditMessageParams,
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "messageThunks/sendOrEditMessage",
  async (
    {
      messageString,
      messageId,
      hint,
      attachments,
      fromInspiration,
      subType,
      entityRef,
      entityRefs,
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    // Only send one message at a time
    const alreadySending = selectIsCustomerMessagePending(getState())
    assert(!alreadySending, "already sending a message")

    // And don't send another until the bot is done responding.
    const listeningToStreams = isListeningToStreams(getState())
    assert(!listeningToStreams, "shouldn't send a message while streaming")

    // The one rule for a live voice call: sending a text message hangs up
    // first (the End button's teardown), so every surface that stays reachable
    // mid-call — ask inputs, sample questions, CTAs — behaves the same way.
    if (selectVoiceModeOpen(getState())) {
      dispatch(voiceChatActions.voiceModeClosed({ reason: "chat_started" }))
    }

    const chatId = selectChatId(getState())
    const chatBotVersion = selectChatBotVersion(getState())
    const locationOfInterest = selectLocationOfInterest(getState())
    const chatApi = new ChatApi(buildClientAxios())

    if (messageId === undefined) {
      dispatch(setFilteredPlaceType(undefined))
    }

    dispatch(userSlice.actions.setPendingCustomerMessage(messageString))
    try {
      let response
      let changedChat: Chat | undefined
      if (fromInspiration) {
        response = await chatApi.addMessageFromInspirationDMO(
          chatId,
          fromInspiration,
          chatBotVersion,
          messageString,
          {
            subType,
          }
        )
      } else if (messageId !== undefined) {
        response = await chatApi.updateCustomerMessage(
          chatId,
          messageId,
          messageString,
          chatBotVersion,
          hint,
          attachments
        )
      } else {
        const addResponse = await chatApi.addCustomerMessage(
          chatId,
          messageString,
          {
            bot: chatBotVersion,
            hint,
            attachments,
            locationOfInterest,
            entityRef,
            entityRefs,
            subType,
          }
        )
        if (addResponse.redirect_to_chat_id) {
          dispatch(incrementSessionChatMessageCount())
          const existingChat = await chatApi.fetchChat(
            addResponse.redirect_to_chat_id
          )
          dispatch(userSlice.actions.setChat(existingChat))
          dispatch(userSlice.actions.patchUser({ has_chatted: true }))
          markChatTabEngagedFromChat(dispatch, existingChat)
          return
        }
        response = addResponse
        changedChat = addResponse.chat
      }

      if (messageId === undefined) {
        dispatch(incrementSessionChatMessageCount())
      }

      if (messageId !== undefined) {
        dispatch(
          userSlice.actions.addOrUpdateMessages({
            messages: [response.message],
          })
        )
      } else {
        dispatch(
          userSlice.actions.addMessage({
            message: response.message,
            chatId,
          })
        )
      }

      if (changedChat && selectChatId(getState()) === chatId) {
        dispatch(userSlice.actions.updateChat(changedChat))
        dispatch(chatsApi.util.invalidateTags(["Chats"]))
      }
      // We're not streaming bot response in group group, although
      // the server supports it.
      const isGroupChat = selectIsGroupChat(getState())
      if (response.stream_id && !isGroupChat) {
        StreamedMessageListener.listenToStream(
          chatId,
          response.stream_id,
          dispatch,
          undefined
        )
      }

      if (messageId === undefined) {
        dispatch(userSlice.actions.patchUser({ has_chatted: true }))
        markChatTabEngaged(dispatch, chatId)
      }

      return response.message
    } catch (error) {
      if (
        error instanceof ClientRateLimitException &&
        error.data.reason === "anon_throttling" &&
        selectUserIsAnonymous(getState())
      ) {
        setAnalyticsUserProperties({
          type: "rateLimitProps",
          rateLimit: { reason: error.data.reason },
        })
        void dispatch(showRegisterGateForAnonymousRateLimiting())
        return
      } else if (
        error instanceof ClientRateLimitException &&
        error.data.reason === "unverified_throttling" &&
        !selectUserIsAnonymous(getState())
      ) {
        setAnalyticsUserProperties({
          type: "rateLimitProps",
          rateLimit: { reason: error.data.reason },
        })
        void dispatch(showRegisterGateForAnonymousRateLimiting())
        return
      } else if (
        error instanceof ClientRateLimitException &&
        error.data.reason === "anon_per_trip_throttling"
      ) {
        setAnalyticsUserProperties({
          type: "rateLimitProps",
          rateLimit: { reason: error.data.reason },
        })
        void dispatch(showRegisterGateForPerTripRateLimiting())
        return
      } else if (error instanceof ClientRateLimitException) {
        setAnalyticsUserProperties({
          type: "rateLimitProps",
          rateLimit: { reason: "rate_limit" },
        })
        void dispatch(temporarilyDisableMessageStreaming())
        return
      } else if (
        error instanceof SimpleHttpError ||
        error instanceof ClientException
      ) {
        return rejectWithValue(error)
      }
      throw error
    } finally {
      dispatch(userSlice.actions.clearPendingCustomerMessage())
    }
  }
)

export const restartBotResponse = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>("messageThunks/restartBotResponse", async (_, { dispatch, getState }) => {
  const chatId = selectChatId(getState())
  const chatBotVersion = selectChatBotVersion(getState())
  const isGroupChat = selectIsGroupChat(getState())
  const chatApi = new ChatApi(buildClientAxios())
  try {
    if (isGroupChat) {
      dispatch(userSlice.actions.setGroupResponseFailed(false))
    }
    const response = await chatApi.restartBotResponse(chatId, chatBotVersion)
    if (response.messages) {
      dispatch(
        userSlice.actions.addOrUpdateMessages({ messages: response.messages })
      )
    } else if (response.stream_id && !isGroupChat) {
      // A group chat follows the restarted reply through the trip broadcasts.
      StreamedMessageListener.listenToStream(
        chatId,
        response.stream_id,
        dispatch,
        undefined
      )
    }
  } catch (error) {
    const userIsAnonymous = selectUserIsAnonymous(getState())
    if (
      error instanceof ClientRateLimitException &&
      error.data.reason === "anon_throttling" &&
      userIsAnonymous
    ) {
      setAnalyticsUserProperties({
        type: "rateLimitProps",
        rateLimit: { reason: error.data.reason },
      })
      void dispatch(showRegisterGateForAnonymousRateLimiting())
    } else if (
      error instanceof ClientRateLimitException &&
      error.data.reason === "unverified_throttling" &&
      !userIsAnonymous
    ) {
      setAnalyticsUserProperties({
        type: "rateLimitProps",
        rateLimit: { reason: error.data.reason },
      })
      void dispatch(showRegisterGateForAnonymousRateLimiting())
    } else if (
      error instanceof ClientRateLimitException &&
      error.data.reason === "anon_per_trip_throttling"
    ) {
      setAnalyticsUserProperties({
        type: "rateLimitProps",
        rateLimit: { reason: error.data.reason },
      })
      void dispatch(showRegisterGateForPerTripRateLimiting())
      return
    } else if (error instanceof ClientRateLimitException) {
      setAnalyticsUserProperties({
        type: "rateLimitProps",
        rateLimit: { reason: "rate_limit" },
      })
      void dispatch(temporarilyDisableMessageStreaming())
    } else {
      toastManager.add({
        type: "error",
        description: "Trouble sending message",
      })
      console.log(error)
    }
  }
})
export const initialBotGreeting = createAsyncThunk<
  void,
  void,
  {
    dispatch: AppDispatch
    state: RootState
  }
>("messageThunks/initialBotGreeting", async (_, { dispatch, getState }) => {
  {
    // Only send one message at a time
    const alreadySending =
      selectIsCustomerMessagePending(getState()) ||
      selectHasInProgressMessage(getState())
    if (alreadySending) return

    // And don't send another until the bot is done responding.
    const listeningToStreams = isListeningToStreams(getState())
    if (listeningToStreams) return

    const chatApi = new ChatApi(buildClientAxios())
    const chatId = selectChatId(getState())
    const geoHint = selectEmbeddedGeoHint(getState())
    const response = await chatApi.initialBotGreeting(chatId, geoHint)
    if (response.stream_id) {
      StreamedMessageListener.listenToStream(
        chatId,
        response.stream_id,
        dispatch,
        "initial-greeting"
      )
    }
  }
})

const startFollowUp = async (
  dispatch: AppDispatch,
  getState: () => RootState,
  rejectWithValue: (value: SimpleHttpError | ClientException) => void,
  context?: FollowUpContext
) => {
  const lastBotMessage =
    context !== undefined
      ? context.lastBotMessage
      : selectLastPersistentBotMessage(getState())
  const lastCustomerMessage =
    context !== undefined
      ? context.lastCustomerMessage
      : selectLastCustomerMessage(getState())
  const chatId =
    context !== undefined ? context.chatId : selectChatId(getState())
  const chatApi = new ChatApi(buildClientAxios())

  try {
    // server auto-detects follow-up type (events, inspiration, quiz, feature_offer, or regular)
    const response = await chatApi.createFollowUp(chatId)
    sendAnalyticsEvent({
      name: "ChatFollowUpMessageRequested",
      properties: {
        lastCustomerMessageId: lastCustomerMessage?.id,
        lastBotMessageId: lastBotMessage.id,
        inspirationId: lastBotMessage?.matching_inspiration_id,
        chat: {
          id: chatId,
        },
      },
    })

    StreamedMessageListener.listenToStream(
      chatId,
      response.stream_id,
      dispatch,
      "follow-up"
    )
  } catch (error) {
    if (error instanceof SimpleHttpError || error instanceof ClientException) {
      return rejectWithValue(error)
    }
    throw error
  }
}

export const executeImmediateFollowUp = createAsyncThunk<
  void,
  void,
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "messageThunks/executeImmediateFollowUp",
  async (_, { dispatch, getState, rejectWithValue }) => {
    // Only send one message at a time
    const alreadySending = selectIsCustomerMessagePending(getState())
    if (alreadySending) return

    // And don't send another until the bot is done responding.
    const listeningToStreams = isListeningToStreams(getState())
    if (listeningToStreams) return

    await startFollowUp(dispatch, getState, rejectWithValue)
  }
)

export const considerAskingBotForFollowUp = createAsyncThunk<
  void,
  void,
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "messageThunks/considerAskingBotForFollowUp",
  async (_, { dispatch, getState, rejectWithValue }) => {
    // Only send one message at a time
    const alreadySending = selectIsCustomerMessagePending(getState())
    if (alreadySending) return

    // And don't send another until the bot is done responding.
    const listeningToStreams = isListeningToStreams(getState())
    if (listeningToStreams) return

    const lastBotMessage = selectLastPersistentBotMessage(getState())
    const lastCustomerMessage = selectLastCustomerMessage(getState())
    const chatId = selectChatId(getState())
    const isMindtripUser = selectIsMindtripUser(getState())

    // We occasionally ask the bot to make a follow-up comment.
    // - only do it after X percent of normal bot responses
    // - only if the bot response was not already a follow-up
    // - after a pause of Y seconds ... and if the customer has
    //   asked a question in those Y seconds, we don't bother
    //   with a follow-up.
    //
    // SPECIAL CASE: if the user is a Mindtrip internal user,
    // we'll skip the X percent check iff the last customer
    // message ends with two periods. If so, we also won't
    // wait quite as long before following up.

    const overrideScarcity =
      isMindtripUser && lastCustomerMessage?.body?.endsWith("..")
    const eligibleForFollowUp =
      lastBotMessage.sub_type !== "follow-up" &&
      lastCustomerMessage != null &&
      lastBotMessage.skip_followup !== true

    // Check if server indicates there's a good follow-up available (e.g., inspiration)
    // Note: events are searched on-demand by the server during follow-up generation
    const hasSuggestionForFollowup =
      lastBotMessage?.has_suggestion_for_followup === true

    const timeForFollowup =
      overrideScarcity || hasSuggestionForFollowup || Math.random() < 0.2

    if (eligibleForFollowUp && timeForFollowup) {
      await wait(overrideScarcity ? 5000 : 120000)
      // Check again to see if the user has asked any questions since we
      // started waiting, started typing, switched chats, or received another
      // bot response. Be sure to use getState() to get fresh state, maybe
      // different from what we saw above.
      if (
        selectChatId(getState()) !== chatId ||
        selectChatInputHasValue(getState()) ||
        selectIsCustomerMessagePending(getState()) ||
        isListeningToStreams(getState()) ||
        selectLastCustomerMessage(getState())?.id !== lastCustomerMessage?.id ||
        selectLastPersistentBotMessage(getState())?.id !== lastBotMessage.id
      ) {
        return
      }

      await startFollowUp(dispatch, getState, rejectWithValue, {
        chatId,
        lastBotMessage,
        lastCustomerMessage,
      })
    }
  }
)

// Freezes the in-flight response locally before asking the backend to stop it,
// so the UI settles without waiting on the round trip.
export const stopBotResponse = createAsyncThunk<
  void,
  number,
  {
    dispatch: AppDispatch
    state: RootState
  }
>("messageThunks/stopBotResponse", async (chatId) => {
  sendAnalyticsEvent("StopBotResponse")
  StreamedMessageListener.stopStreamingForChat(chatId)

  const chatApi = new ChatApi(buildClientAxios())
  await chatApi.stopResponse(chatId)
})

// A group chat's reply isn't streamed to the client, so stopping it settles the
// local "responding" state at once instead of waiting for the server to wind
// down (which can take a while if a tool is running).
export const stopGroupBotResponse = createAsyncThunk<
  void,
  number,
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "messageThunks/stopGroupBotResponse",
  async (chatId, { dispatch, getState }) => {
    sendAnalyticsEvent("StopBotResponse")
    selectStreamIdsBotIsRespondingToMe(getState()).forEach((streamId) => {
      // Marked stopped so the end broadcast isn't mistaken for a failure
      dispatch(userSlice.actions.markStreamStopped(streamId))
      dispatch(removePendingBotResponse(streamId))
    })

    const chatApi = new ChatApi(buildClientAxios())
    await chatApi.stopResponse(chatId)
  }
)

export const temporarilyDisableMessageStreaming = createAsyncThunk<
  void,
  void,
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "messageThunks/temporarilyDisableMessageStreaming",
  async (_, { dispatch }) => {
    dispatch(userSlice.actions.stopMessageStreamingAndDisable())
    setTimeout(
      () => dispatch(userSlice.actions.enableChatMessages()),
      60 * 1000
    )
  }
)

export const showRegisterGateForAnonymousRateLimiting = createAsyncThunk<
  void,
  void,
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "messageThunks/showRegisterGateForAnonymousRateLimiting",
  async (_, { dispatch }) => {
    void dispatch(showRegisterGateDialog("message"))
    dispatch(userSlice.actions.setAnonymousUserWasRateLimited())
  }
)

export const showRegisterGateForPerTripRateLimiting = createAsyncThunk<
  void,
  void,
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "messageThunks/showRegisterGateForPerTripRateLimiting",
  async (_, { dispatch }) => {
    void dispatch(showRegisterGateDialog("message-per-trip"))
    dispatch(userSlice.actions.setAnonymousUserWasRateLimited())
  }
)
